import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminService } from "../services/admin.service";

export const adminKeys = {
  overview: ["admin", "overview"],
  attention: ["admin", "attention"],
  users: (params = {}) => ["admin", "users", params],
  transfers: (params = {}) => ["admin", "transfers", params],
  transferDetail: (id) => ["admin", "transfer", id],
  compliance: (params = {}) => ["admin", "compliance", params],
  sanctionsMatches: (params = {}) => ["admin", "sanctions-matches", params],
  amlDecisions: (params = {}) => ["admin", "aml-risk-decisions", params],
  auditLogs: (params = {}) => ["admin", "audit-logs", params],
  roles: ["admin", "roles"],
  myAuditLogs: (params = {}) => ["admin", "me", "audit-logs", params],
  routingCorridors: ["admin", "routing", "corridors"],
  configuration: ["admin", "configuration"],
};

/**
 * Invalidate the resource a mutation touched, plus the two views that every
 * privileged mutation affects: the dashboard counters and the audit trail.
 *
 * Deliberately narrow - blanket-invalidating all of `["admin"]` would refetch
 * unrelated lists on every edit.
 */
function invalidateAfterMutation(queryClient, ...resourceKeys) {
  [...resourceKeys, ["admin", "audit-logs"], adminKeys.overview].forEach((queryKey) =>
    queryClient.invalidateQueries({ queryKey })
  );
}

/**
 * Grantable roles and what each confers.
 *
 * Served from the backend permission registry rather than hardcoded here: a
 * duplicated list silently stops offering roles that exist, and offers roles
 * that no longer do. Rarely changes, so it is cached for the session.
 */
/**
 * Your own audited actions. Needs no `audit:read` - see the route docstring.
 */
export function useMyAuditLogs(params = {}) {
  const query = useQuery({
    queryKey: adminKeys.myAuditLogs(params),
    queryFn: () => adminService.listMyAuditLogs(params),
    retry: false,
  });
  return {
    entries: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
  };
}

export function useAdminRoles({ enabled = true } = {}) {
  const { data, isLoading, error } = useQuery({
    queryKey: adminKeys.roles,
    queryFn: adminService.listRoles,
    staleTime: Infinity,
    enabled,
    retry: false,
  });
  return { roles: data ?? [], isLoadingRoles: isLoading, rolesError: error };
}

/**
 * The operator attention queue.
 *
 * Polled on a slow interval rather than pushed: the backend has no scheduler,
 * so there is nothing to subscribe to. A minute is frequent enough for a queue
 * a human works through and cheap enough to leave running on every admin page.
 */
export function useAdminAttention({ enabled = true } = {}) {
  const query = useQuery({
    queryKey: adminKeys.attention,
    queryFn: adminService.getAttention,
    staleTime: 60_000,
    refetchInterval: 60_000,
    enabled,
    retry: false,
  });

  return {
    buckets: query.data?.buckets ?? [],
    total: query.data?.total ?? 0,
    urgentTotal: query.data?.urgent_total ?? 0,
    stuckAfterMinutes: query.data?.stuck_after_minutes ?? null,
    isLoadingAttention: query.isLoading,
    attentionError: query.error,
  };
}

export function useAdminOverview() {
  return useQuery({
    queryKey: adminKeys.overview,
    queryFn: adminService.getOverview,
    staleTime: 30_000,
  });
}

/**
 * Paginated, server-side-filtered user list.
 *
 * Filtering happens in SQL rather than the browser so search stays correct
 * once there are more users than fit on one page.
 */
export function useAdminUsers(params = {}) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: adminKeys.users(params),
    queryFn: () => adminService.listUsers(params),
    keepPreviousData: true,
  });

  const usersKey = ["admin", "users"];

  const updateMutation = useMutation({
    mutationFn: ({ userId, payload }) => adminService.updateUser(userId, payload),
    onSuccess: () => invalidateAfterMutation(queryClient, usersKey),
  });

  const accountStatusMutation = useMutation({
    mutationFn: ({ userId, status, reason }) =>
      adminService.updateAccountStatus(userId, { status, reason }),
    // Suspension also removes the account from the compliance queue's view.
    onSuccess: () =>
      invalidateAfterMutation(queryClient, usersKey, adminKeys.compliance),
  });

  const roleMutation = useMutation({
    mutationFn: ({ userId, role, reason }) =>
      adminService.updateUserRole(userId, { role, reason }),
    onSuccess: () => invalidateAfterMutation(queryClient, usersKey),
  });

  return {
    ...query,
    users: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    updateUser: updateMutation.mutateAsync,
    isUpdatingUser: updateMutation.isPending,
    updateAccountStatus: accountStatusMutation.mutateAsync,
    isUpdatingAccountStatus: accountStatusMutation.isPending,
    updateUserRole: roleMutation.mutateAsync,
    isUpdatingRole: roleMutation.isPending,
  };
}

export function useAdminTransfers(params = {}) {
  const query = useQuery({
    queryKey: adminKeys.transfers(params),
    queryFn: () => adminService.listTransfers(params),
    refetchInterval: 30_000,
    keepPreviousData: true,
  });

  return {
    ...query,
    transfers: query.data?.items ?? [],
    total: query.data?.total ?? 0,
  };
}

/**
 * Paginated, server-side-filtered compliance queue.
 *
 * The counters come from the response rather than from `cases.length`: derived
 * in the browser they would report the size of the current page, so a queue
 * larger than one page would under-report indefinitely.
 */
export function useAdminCompliance(params = {}) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: adminKeys.compliance(params),
    queryFn: () => adminService.listComplianceCases(params),
    keepPreviousData: true,
  });

  const updateMutation = useMutation({
    mutationFn: ({ userId, status, reason }) =>
      adminService.updateComplianceStatus(userId, { status, reason }),
    onSuccess: () =>
      invalidateAfterMutation(queryClient, ["admin", "compliance"], ["admin", "users"]),
  });

  return {
    ...query,
    cases: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    totalOpen: query.data?.total_open ?? 0,
    totalHighRisk: query.data?.total_high_risk ?? 0,
    totalBlocked: query.data?.total_blocked ?? 0,
    updateComplianceStatus: updateMutation.mutateAsync,
    isUpdatingCompliance: updateMutation.isPending,
  };
}

/** Paginated sanctions candidates and their human-review lifecycle. */
export function useAdminSanctionsMatches(params = {}) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: adminKeys.sanctionsMatches(params),
    queryFn: () => adminService.listSanctionsMatches(params),
    keepPreviousData: true,
  });

  const reviewMutation = useMutation({
    mutationFn: ({ matchId, disposition, reason }) =>
      adminService.reviewSanctionsMatch(matchId, { disposition, reason }),
    onSuccess: () =>
      invalidateAfterMutation(
        queryClient,
        ["admin", "sanctions-matches"],
        ["admin", "compliance"]
      ),
  });

  return {
    ...query,
    matches: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    reviewSanctionsMatch: reviewMutation.mutateAsync,
    isReviewingSanctionsMatch: reviewMutation.isPending,
  };
}

/** Internal AML decisions for one customer - evidence behind a compliance case. */
export function useAmlRiskDecisions(senderUserId, { enabled = true } = {}) {
  return useQuery({
    queryKey: adminKeys.amlDecisions({ senderUserId }),
    queryFn: () => adminService.listAmlRiskDecisions({ senderUserId }),
    enabled: Boolean(senderUserId) && enabled,
    retry: false,
  });
}

export function useAdminAuditLogs(params = {}) {
  const query = useQuery({
    queryKey: adminKeys.auditLogs(params),
    queryFn: () => adminService.listAuditLogs(params),
    keepPreviousData: true,
  });

  return {
    ...query,
    entries: query.data?.items ?? [],
    total: query.data?.total ?? 0,
  };
}

export function useAdminSettings() {
  const configuration = useQuery({
    queryKey: adminKeys.configuration,
    queryFn: adminService.getConfiguration,
  });

  return {
    configuration: configuration.data ?? null,
    isLoading: configuration.isLoading,
    error: configuration.error,
  };
}

/**
 * Every offered corridor with the routes that carry it and, when none can,
 * why. Routes are computed from each provider's code on the server, so there
 * is nothing to mutate here.
 */
export function useAdminRoutingCorridors({ enabled = true } = {}) {
  const query = useQuery({
    queryKey: adminKeys.routingCorridors,
    queryFn: adminService.listRoutingCorridors,
    enabled,
    retry: false,
  });

  return {
    corridors: query.data ?? [],
    isLoadingCorridors: query.isLoading,
    corridorsError: query.error,
  };
}
